import React from 'react';
import PropTypes from 'prop-types';
import PasswordInput from 'components/atoms/PasswordInput';
import SelectField from '../SelectField';
import { useCurrency } from 'contexts/CurrencyContext';
import { formatDocument, DOCUMENT_TYPES_BY_COUNTRY } from 'util/stringFormat';

const formatDisplay = (val) => {
  if (val === null || val === undefined || val === '') return '';
  const stringVal = val.toString();
  const [integer, decimal] = stringVal.split('.');
  const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return decimal !== undefined ? `${formattedInteger},${decimal}` : formattedInteger;
};

const parseRaw = (val) => {
  if (!val) return '';
  return val.replace(/\./g, '').replace(',', '.');
};

function FormField({
  asyncList,
  className = '',
  errors,
  hideErrorMessage,
  label,
  labelClassName = '',
  errorClassName = '',
  name,
  options,
  register = () => ({}),
  size = '',
  type,
  value,
  isCurrency = false,
  isDocument = false,
  isHidden = false,
  ...rest
}) {
  const hasError = errors?.[name];
  const errorMessage = errors?.[name]?.message;

  const { active } = useCurrency();
  const currentCountry = active?.country;

  const registerOptions = isCurrency
    ? {
        setValueAs: (v) => {
          if (!v) return null;
          const cleaned = parseRaw(v);
          return isNaN(parseFloat(cleaned)) ? null : parseFloat(cleaned);
        },
      }
    : isDocument
    ? {
        setValueAs: (v) => (v ? v.replace(/[^\d]/g, '') : ''),
      }
    : {};

  const registerProps = register(name, registerOptions) || {};

  const validator = (event) => {
    const digitPeriodRegExp = isCurrency
      ? new RegExp('\\d|\\.|\\,')
      : isDocument
      ? new RegExp('\\d')
      : new RegExp(rest.allowNegative ? '\\d|\\.|\\-' : '\\d|\\.|\\,');

    if (event.ctrlKey || event.altKey || typeof event.key !== 'string' || event.key.length !== 1) {
      return;
    }

    if (!digitPeriodRegExp.test(event.key)) {
      event.preventDefault();
      return;
    }

    if (isDocument) {
      const cleanLen = event.target.value.replace(/[^\d]/g, '').length;
      if (cleanLen >= 11) {
        event.preventDefault();
      }
    }
  };

  if (isHidden) {
    return <input type="hidden" {...registerProps} />;
  }

  const inputCollection = {
    password: <PasswordInput register={register} name={name} className={className} size={size} {...rest} />,
    date: (
      <input
        className={`form-field__input ${className}`}
        type="date"
        {...rest}
        {...registerProps}
        {...(value ? { value } : {})}
      />
    ),
    text: <input className={`form-field__input ${className}`} type={type} {...rest} {...registerProps} />,
    number: (
      <input
        className={`form-field__input ${className}`}
        {...rest}
        autoComplete="new-password"
        type={isCurrency || isDocument ? 'text' : 'number'}
        inputMode={isCurrency || isDocument ? 'decimal' : undefined}
        {...registerProps}
        onChange={(e) => {
          let inputValue = e.target.value;

          if (isCurrency) {
            if (inputValue === '') {
              e.target.value = '';
            } else {
              const raw = parseRaw(inputValue);
              const regex = /^\d{0,15}(\.\d{0,2})?$/;
              if (regex.test(raw) || raw.endsWith('.')) {
                e.target.value = formatDisplay(raw);
              } else {
                e.target.value = formatDisplay(parseRaw(e.target.defaultValue || ''));
              }
            }
          } else if (isDocument) {
            const cleanValue = inputValue.replace(/[^\d]/g, '').slice(0, 11);
            const documentType = DOCUMENT_TYPES_BY_COUNTRY[currentCountry];
            e.target.value = formatDocument(cleanValue, documentType, currentCountry);
          }

          if (registerProps.onChange) registerProps.onChange(e);
        }}
        onKeyPress={validator}
      />
    ),
  };

  return (
    <div className={`form-field ${hasError ? 'error' : ''} ${size}`}>
      {label && (
        <label className={`form-field__label ${labelClassName}`} htmlFor={name}>
          {label}
        </label>
      )}
      {type === 'select' ? (
        <SelectField asyncList={asyncList} label={label} name={name} options={options} register={register} {...rest} />
      ) : (
        inputCollection[type ?? 'text'] || inputCollection.text
      )}
      {errorMessage && !hideErrorMessage && (
        <span className={`form-field__error-message ${errorClassName} text-red`}>{errorMessage}</span>
      )}
    </div>
  );
}

FormField.propTypes = {
  asyncList: PropTypes.arrayOf(PropTypes.object),
  className: PropTypes.string,
  errors: PropTypes.object,
  label: PropTypes.string,
  labelClassName: PropTypes.string,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.object),
  register: PropTypes.func,
  size: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.any,
  isCurrency: PropTypes.bool,
  isDocument: PropTypes.bool,
  isHidden: PropTypes.bool,
};

export default FormField;
